// ─────────────────────────────────────────────────────────────────────────
//  ClickViewer · web · 로그인 상태
//
//  ★★★ 이 파일의 규칙 하나만 기억하면 된다 ★★★
//
//    **로그인 상태를 localStorage 에 저장하지 않는다.**
//
//  세션은 HttpOnly 쿠키(`cv_s`)에 있고 JS 는 그걸 **읽을 수 없다**(그게 HttpOnly 의 목적이다).
//  그래서 "로그인했다" 는 표시를 localStorage 에 따로 두면, 그 사본은 반드시 언젠가
//  진실과 어긋난다 — 다른 탭에서 로그아웃했거나, 30일이 지났거나, 관리자가 계정을 막았거나.
//  그때 화면은 **로그인된 것처럼 보이는데 모든 요청이 401** 인 최악의 상태가 되고,
//  사용자는 "화면은 멀쩡한데 아무것도 안 된다" 고 말한다. 그건 디버깅이 거의 불가능하다.
//
//  → 앱이 시작할 때 `GET /api/me` 를 **한 번** 부른다. 그 응답이 유일한 진실 원천이다.
//    200 이면 로그인, 401 이면 로그인 화면. 그 밖이면 **오류이지 로그아웃이 아니다.**
//
//  ★ 그리고 **네트워크 실패와 401 을 절대 뭉개지 않는다.**
//    현장에서 신호가 나쁜 것과 세션이 끊긴 것은 사용자가 해야 할 일이 정반대다.
//    (전자: 기다렸다 다시 / 후자: 다시 로그인). 뭉개면 신호 나쁜 사람이 로그인 화면으로
//    쫓겨나고, 코드를 다시 받아도 안 되니 그 사람은 시스템을 못 쓴다고 결론 내린다.
// ─────────────────────────────────────────────────────────────────────────

import {
  createContext,
  useRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  ApiError,
  getMe,
  getProjects,
  logout as apiLogout,
  setUnauthorizedHandler,
  type Project,
  type User,
} from '../api/client';

export type SessionState =
  /** 아직 `/api/me` 의 답이 안 왔다. 앱 시작 직후 잠깐. */
  | { status: 'loading' }
  /** 401 을 받았다 = 로그인이 필요하다. **오류가 아니다.** */
  | { status: 'anon' }
  /**
   * 로그인돼 있다. projects 가 빈 배열일 수 있다 — 그것도 정상이다(§7-5).
   *
   * ★★ `permsStale` [2026-08-12] — **`perms[]` 를 끝내 못 받은 현장이 목록에 있다**는 표시.
   *   `/api/projects` 가 한 번 실패해 `/api/me` 의 목록으로 넘어간 경우다.
   *   그 목록에는 perms 가 없어 뷰어의 게이트가 전부 닫히는데, 그건 **관리자가 끈 것이
   *   아니라 신호가 끊긴 것**이다. 두 경우를 화면이 다르게 말해야 해서 여기서 표시한다
   *   (`viewer/data/caps.ts` 의 `offGateNote`).
   *
   * ⚠️ [고친 것 2026-08-12 · 같은 날 두 번째] 예전에는 실패하면 **무조건** true 였고,
   *   그러면서 **이미 받아 둔 perms 까지 같이 버렸다.** 뷰어를 열어 둔 사람이 목록
   *   새로고침을 한 번 했다가 신호가 끊기면, 방금까지 쓰던 게이트가 통째로 닫히고
   *   상태줄이 「권한을 못 받았습니다」로 바뀌었다 — perms 는 몇 초 전에 받아서
   *   **손에 들고 있었는데도** 그랬다. 지금은 `mergePerms` 가 옛 perms 를 이어 붙이고,
   *   **정말로 모르는 현장이 남았을 때만** true 다.
   */
  | { status: 'authed'; user: User; projects: Project[]; permsStale?: boolean }
  /** 401 이 **아닌** 실패. 서버가 안 떴거나, 신호가 없거나, 서버가 아프다. */
  | { status: 'error'; error: ApiError };

interface SessionValue {
  state: SessionState;
  /**
   * `/api/me` 를 다시 읽는다. 로그인 직후·「다시 시도」·목록 새로고침이 전부 이걸 부른다.
   *
   * ★★ **이미 로그인된 상태에서는 `loading` 으로 되돌아가지 않는다.** `refreshing` 만 켠다.
   *
   *    왜 이게 중요하냐면 — `App.tsx` 가 `status === 'loading'` 일 때 **다른 요소를 반환**한다.
   *    즉 잠깐이라도 loading 으로 내려가면 그 아래 트리가 **통째로 언마운트**되고,
   *    뷰어가 열려 있었다면 **WebGL 컨텍스트·카메라 위치·켜 둔 레이어가 전부 날아간다.**
   *    목록 새로고침 한 번, 아무 401 하나로 그렇게 된다.
   *    (`src/viewer/react/ViewerProvider.tsx` 머리말 규칙 ⑤)
   */
  reload: () => Promise<void>;
  /** 로그인된 채로 목록을 다시 읽는 중인가. 화면은 유지하고 표시만 바꾼다. */
  refreshing: boolean;
  /** 로그아웃. 서버 호출이 실패해도 화면은 로그아웃된 것처럼 만든다(아래 주석). */
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const v = useContext(SessionContext);
  // Provider 밖에서 부르면 «undefined 를 읽을 수 없다» 같은 엉뚱한 오류로 죽는다. 먼저 잡는다.
  if (!v) throw new Error('useSession 은 <SessionProvider> 안에서만 쓸 수 있다');
  return v;
}

/**
 * `/api/projects` 가 실패했을 때 **화면에 넘길 목록**을 만든다.
 * 목록 자체는 `me` 것(가입·탈퇴가 반영된 최신)을 쓰고, **`perms` 만 옛것을 이어 붙인다.**
 *
 * ★ 왜 이어 붙이나 [2026-08-12]
 *   `/api/me` 의 목록에는 `perms[]` 가 **없다**(`apps/api/src/routes/auth.ts` 의 `getMe` 가
 *   일부러 안 넣는다 — 그 함수 끝의 주석에 이유가 있다). 그래서 실패했을 때 그 목록으로
 *   그냥 갈아 끼우면 `capsOf(undefined)` → `NO_CAPS` 가 되어 **뷰어 게이트가 전부 닫힌다.**
 *
 *   ⚠️ 그게 「신호가 끊겼다」를 화면에 말해 주는 것까지는 맞는데, **이미 알고 있던 것까지
 *      버린다.** 뷰어를 열어 둔 사람이 목록 새로고침 한 번 했다가 LTE 음영에 들어가면
 *      단면·트리·4D 가 한꺼번에 사라진다. 방금 받아 둔 perms 가 메모리에 그대로 있는데도다.
 *      (저장소 규율 — 「기능을 꺼서 해결하지 마라」)
 *
 *   ★ 옛 perms 를 잠깐 쓰는 것이 안전한 이유: **진짜 판정은 서버가 한다.** 권한이 빠졌으면
 *     그 API 가 404 를 준다(`App.tsx` 의 `ProjectRoute` 주석 — 「화면의 판정은 편의이지
 *     보안이 아니다」). 반대로 게이트를 닫아 버리면 **사용자에게 되돌릴 방법이 없다.**
 *
 * ★ `permsStale` 은 **이어 붙이고도 모르는 현장이 남았을 때만** true 다.
 *   ⚠️ 현장 하나만 몰라도 true 인 **한 덩이 표시**다. 즉 뷰어가 연 현장은 perms 를
 *      이어받았는데 새로 생긴 다른 현장 하나 때문에 true 가 될 수 있다 — 그때 상태줄이
 *      「권한을 못 받았습니다」를 한 번 더 말한다. **그 방향의 오차는 일부러 남긴 것이다.**
 *      틀려도 사용자가 할 일은 「새로고침」으로 같지만, 반대 방향(꺼졌다고 단정)은
 *      **관리자에게 헛걸음**시킨다. 현장별로 가르고 싶으면 `App.tsx` 의 `ProjectRoute` 가
 *      `project.perms === undefined` 를 같이 보면 된다 — 거기가 이미 현장 하나를 고른 자리다.
 *
 * ⚠️ `perms: []` 와 `perms: undefined` 는 **다른 값이다.** 앞은 「정말 아무것도 없다」이고
 *    뒤는 「모른다」다. 여기서 둘을 뭉개면 권한이 하나도 없는 사람이 영원히
 *    「신호가 끊겼습니다, 새로고침하세요」를 보게 된다 — 새로고침해도 안 바뀐다.
 */
export function mergePerms(
  fresh: readonly Project[],
  prev: readonly Project[] | undefined,
): { projects: Project[]; permsStale: boolean } {
  /** 옛 목록에서 **알고 있던** perms 만. `undefined`(모름)는 담지 않는다. */
  const known = new Map<string, string[]>();
  for (const p of prev ?? []) if (p.perms !== undefined) known.set(p.id, p.perms);

  let permsStale = false;
  const projects = fresh.map((p) => {
    if (p.perms !== undefined) return p;
    const carried = known.get(p.id);
    if (carried !== undefined) return { ...p, perms: carried };
    // 이 현장은 이어 붙일 옛 값도 없다 = **정말로 모른다.** 화면이 그렇게 말해야 한다.
    permsStale = true;
    return p;
  });
  return { projects, permsStale };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'loading' });
  const [refreshing, setRefreshing] = useState(false);

  // 지금 상태를 effect 없이 읽기 위한 거울. setState 로 상태를 갈아 끼울 때
  // 「이미 로그인돼 있었나」를 알아야 하는데, state 를 useCallback 의존성에 넣으면
  // reload 가 매번 새로 만들어져 아래 초기 1회 effect 가 무한히 다시 돈다.
  const stateRef = useRef<SessionState>(state);
  stateRef.current = state;

  /**
   * **지금 살아 있는 세션 시도의 번호.** [2026-08-12]
   *
   * ★ 왜 필요한가: `reload()` 는 왕복 두 번(`/api/me` → `/api/projects`)이라 **느리다**.
   *   그 사이에 사용자가 로그아웃할 수 있고, 실제로 그럴 수 있는 화면이 있다 —
   *   `screens/Invite.tsx` 는 effect 에서 `reload()` 를 돌리면서 「다른 계정으로 로그인」
   *   단추를 같이 그린다. 그때 예전 코드는 이렇게 됐다:
   *
   *       signOut() → setState(anon)  →  (뒤늦게) reload 가 setState(authed)
   *
   *   = **로그아웃했는데 다시 로그인된 화면으로 돌아온다.** 공용 PC·현장 태블릿에서
   *   그대로 자리를 뜨는 사람이 생기는, 이 파일이 `signOut` 주석에서 막으려던 바로 그 상태다.
   *
   * ★ 그리고 같은 장치가 **reload 끼리의 경합**도 막는다. 새로고침을 두 번 누르면
   *   먼저 시작한 느린 응답이 나중에 도착해 새 값을 덮어쓸 수 있었다 — 그 느린 쪽이
   *   `/api/projects` 실패였다면 **멀쩡한 perms 를 방금 지운 셈**이 된다.
   *
   * → 규칙 하나: **번호를 올린 쪽이 이긴다.** 늦게 도착한 시도는 자기 값을 조용히 버린다.
   *   올리는 곳은 **딱 둘** — `reload` 가 시작할 때, `signOut` 이 서버를 부르기 전에.
   *
   * ⚠️ **401 처리(`setUnauthorizedHandler`)는 일부러 안 올린다.** 올리면 로그인 직후의
   *    `reload()` 가 옛 요청의 뒤늦은 401 때문에 결과를 버리고 **로그인 화면에 갇힌다.**
   *    자세한 이유는 그 handler 자리에 적어 뒀다. 지금 「빠뜨렸네」 하고 채우지 마라.
   */
  const epochRef = useRef(0);

  const reload = useCallback(async () => {
    // ★ 이미 로그인돼 있으면 화면을 내리지 않는다. 위 reload 주석 참고.
    const wasAuthed = stateRef.current.status === 'authed';
    // ★★ 이 시도의 번호를 찍는다. 이후 setState 는 전부 `stillMine()` 을 지나야 한다.
    const epoch = ++epochRef.current;
    /** 이 시도가 아직 **최신**인가. 아니면 여기서 나온 값은 버린다(위 `epochRef`). */
    const stillMine = () => epochRef.current === epoch;

    if (wasAuthed) setRefreshing(true);
    else setState({ status: 'loading' });
    try {
      const me = await getMe();

      // ★★ `/api/me` 의 목록에는 **`perms[]` 가 없다**
      //    (`apps/api/src/routes/auth.ts` 의 `getMe` 가 일부러 안 넣는다. 이유는 그 함수
      //     끝의 주석에 있다 — 조직관리자·superadmin 은 역할 행이 없어서, role_permission
      //     만 긁으면 그 두 사람의 화면에서 버튼이 통째로 사라진다.)
      //    ⚠️ 여기 예전에는 `auth.ts:281` 이라고 **줄 번호**가 적혀 있었다. 그 파일이
      //       자라면서 그 줄은 엉뚱한 곳(코드 한복판)을 가리키게 됐다 — 실제 자리는
      //       `getMe` 의 끝이다. **줄 번호로 다른 파일을 가리키지 마라. 심볼 이름을 적어라.**
      //    그래서 여기서 `/api/projects` 를 한 번 더 읽어 그것으로 목록을 갈아 끼운다.
      //    안 하면 `App.tsx` 가 `project.perms` 로 **항상 undefined** 를 넘기고,
      //    `can()` 은 전부 false 가 되어 **버튼이 조용히 하나도 안 뜬다.**
      //    (오류가 안 나서 아무도 못 찾는 종류의 버그다.)
      //
      //  ⚠️ 부르는 자리는 **여기 한 곳뿐이다.** 화면마다 각자 부르면 LTE 에서 왕복이 3번 된다.
      let projects = me.projects;
      let permsStale = false;
      try {
        projects = await getProjects();
      } catch (e2) {
        // 401 이면 세션이 그 사이 끊긴 것이다 — 아래 공통 처리로 넘긴다.
        if (e2 instanceof ApiError && e2.kind === 'unauthorized') throw e2;
        // 그 밖(신호 끊김·서버 아픔)은 **로그인 자체를 깨지 않는다.** `me` 의 목록으로 간다.
        //
        // ★★ [2026-08-12] **그 사실을 화면에 넘긴다.** 예전에는 여기서 그냥 삼켰고,
        //    그 결과 뷰어 상태줄이 「이 현장에서 꺼진 기능 13개」라고 말했다.
        //    관리자는 아무것도 안 껐는데도 그렇게 말하니, 사용자는 그 문장을 믿고
        //    **관리자에게 헛걸음**했다. 새로고침 한 번이면 낫는 일이었다.
        //    → `viewer/data/caps.ts` 의 `offGateNote(caps, permsStale)` 이 문장을 가른다.
        //
        // ★★ 그리고 **알고 있던 perms 는 버리지 않는다.** 이어 붙이고도 모르는 현장이
        //    남았을 때만 `permsStale` 이 켜진다 — 이유는 `mergePerms` 의 주석에 있다.
        const before = stateRef.current;
        const merged = mergePerms(me.projects, before.status === 'authed' ? before.projects : undefined);
        projects = merged.projects;
        permsStale = merged.permsStale;
      }

      // ★ 늦게 도착한 시도는 여기서 조용히 사라진다(위 `epochRef`).
      //   안 그러면 로그아웃한 사람이 다시 로그인된 화면으로 돌아온다.
      if (stillMine()) setState({ status: 'authed', user: me.user, projects, permsStale });
    } catch (e) {
      if (e instanceof ApiError && e.kind === 'unauthorized') {
        // ★ 401 은 「오류」가 아니라 「아직 로그인 안 함」이다. 화면에 빨간 글씨를 띄우지 않는다.
        //   ⚠️ `stillMine()` 이 false 라 여기를 못 지나가도 괜찮다. 그 뜻은 **더 새로운
        //      `reload` 나 `signOut` 이 이미 상태를 정했다**는 것이고, 그쪽이 더 새것이다.
        //      (`setUnauthorizedHandler` 는 번호를 안 올린다 — 그쪽 주석 참고.)
        if (stillMine()) setState({ status: 'anon' });
        return;
      }
      // ★ 그 밖은 전부 오류다. **여기서 anon 으로 뭉개지 마라.**
      //   그러면 API 가 안 떠 있을 때 로그인 화면이 뜨고, 코드를 아무리 요청해도
      //   아무 일이 안 일어난다 — 원인이 화면 어디에도 안 나온다.
      if (stillMine()) {
        setState({ status: 'error', error: e instanceof ApiError ? e : new ApiError('server', 0, null, null) });
      }
    } finally {
      // ⚠️ 남의 시도가 켜 둔 「읽는 중」을 내가 끄면 **돌고 있는데 다 된 것처럼 보인다.**
      if (stillMine()) setRefreshing(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    // ★★ **서버를 부르기 전에** 번호를 올린다. 이 순간 날아다니던 `reload()` 는 전부
    //    자기 값을 버린다 — 안 그러면 로그아웃 뒤에 authed 가 늦게 도착한다(위 `epochRef`).
    epochRef.current++;
    // 그 버려질 시도가 켜 둔 「읽는 중」을 대신 끈다(그 시도의 finally 는 이제 안 지나간다).
    setRefreshing(false);
    try {
      await apiLogout();
    } catch {
      // ⚠️ 실패해도 삼킨다. 여기서 "로그아웃 실패" 를 띄우고 화면에 남겨 두면
      //    공용 PC·현장 태블릿에서 그대로 자리를 뜨는 사람이 생긴다.
      //    쿠키는 서버가 지우지만, 못 지웠더라도 화면에서 내리는 쪽이 안전하다.
      //    (다음 요청이 401 이면 어차피 같은 자리로 온다.)
    }
    setState({ status: 'anon' });
  }, []);

  // ─ 앱 시작 ────────────────────────────────────────────────────────────
  useEffect(() => {
    void reload();
    // ⚠️ dev 의 StrictMode 는 이 effect 를 **두 번** 돌린다 → /api/me 가 두 번 간다.
    //    GET 이고 부작용이 없어 문제가 안 되지만, 네트워크 탭에서 두 줄이 보인다고
    //    "재시도 루프인가" 하고 찾지 마라. 실서버 빌드에서는 한 번이다.
  }, [reload]);

  // ─ 요청 중 세션이 끊긴 것을 한 곳에서 받는다 ──────────────────────────
  useEffect(() => {
    setUnauthorizedHandler(() => {
      // 다른 탭에서 로그아웃했거나 30일이 지났다. 조용히 로그인 화면으로 돌아간다.
      // (로그인 화면 자신의 401 은 여기로 안 온다 — client.ts 의 silent401 참고.)
      //
      // ★★ [2026-08-12] ⚠️ **여기서는 `epochRef` 를 올리지 않는다. 일부러다.**
      //    올리면 날아다니던 `reload()` 가 자기 결과를 버리는데, 그게 **로그인을 막는다**:
      //
      //      옛 세션이 끊김 → (뷰어의 느린 요청이 아직 날아다님) → 로그인 화면 →
      //      코드 입력 성공(새 쿠키) → `Login.tsx` 가 `reload()` →
      //      그 사이 **옛 요청**이 뒤늦게 401 로 떨어짐 → 여기가 번호를 올림 →
      //      방금 200 을 받은 `reload()` 가 결과를 버림 → **로그인 화면에 그대로 갇힌다.**
      //
      //    맞는 코드를 쳤는데 아무 일도 안 일어나니 사용자는 시스템을 못 쓴다고 결론 낸다
      //    (이 파일 머리말이 막으려는 바로 그 상태다).
      //
      //    ★ 판정 기준은 머리말 그대로다 — **`/api/me` 의 200 이 유일한 진실 원천이다.**
      //      남의 요청 하나가 401 을 받은 것보다, 방금 세션을 직접 물어본 답이 더 새것이다.
      //      세션이 정말 죽었다면 그 `reload()` 의 `getMe` 도 401 을 받아 여기로 온다.
      //
      //    ⚠️ `signOut` 은 반대다. 그쪽은 **사용자가 시킨 일**이라 되살아나면 안 된다 —
      //       그래서 거기서만 번호를 올린다(위 `epochRef`).
      setState({ status: 'anon' });
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const value = useMemo<SessionValue>(
    () => ({ state, reload, refreshing, signOut }),
    [state, reload, refreshing, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
